import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { addExtraReducer } from '../../../../store/addExtraReducer'
import { LoadingStatusEnum } from '../../../../store/types'
import { SortDirectionCapital } from '../../../../types'
import { CommissionBenchmarkOverviewColumnsEnum } from '../api/types'
import { DetailSortOption } from '../components/AccountDetailsTable'

import initialState, { createDefaultDetailState, OverviewFilterKey } from './initialState'
import {
  GetCommissionBenchmarkAccountBreakdownReducer,
  GetCommissionBenchmarkOverviewReducer,
  GetCommissionBenchmarkReferenceReducer,
} from './reducers'
import {
  GetCommissionBenchmarkAccountBreakdown,
  GetCommissionBenchmarkOverview,
  GetCommissionBenchmarkReference,
  SLICE_NAME,
} from './thunks'

const CommissionBenchmarkSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    toggleAccountExpanded: (state, action: PayloadAction<number>) => {
      const accountId = action.payload
      state.expandedAccounts = state.expandedAccounts.includes(accountId)
        ? state.expandedAccounts.filter((id) => id !== accountId)
        : [...state.expandedAccounts, accountId]
    },
    toggleAccountDetailOnlyWorse: (state, action: PayloadAction<number>) => {
      const accountId = action.payload
      const current = state.details[accountId] ?? createDefaultDetailState()

      state.details[accountId] = {
        ...current,
        onlyWorse: !current.onlyWorse,
      }
    },
    setAccountDetailSortBy: (
      state,
      action: PayloadAction<{ accountId: number; sortBy: DetailSortOption }>,
    ) => {
      const { accountId, sortBy } = action.payload
      const current = state.details[accountId] ?? createDefaultDetailState()

      state.details[accountId] = {
        ...current,
        sortBy,
      }
    },
    toggleOverviewFilter: (state, action: PayloadAction<OverviewFilterKey>) => {
      const key = action.payload
      state.overviewFilters[key] = !state.overviewFilters[key]
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = 1
    },
    updateOverviewPageSize: (state, action: PayloadAction<number>) => {
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = 1
      state.overviewPagination.perPage = action.payload
    },
    updateOverviewPage: (state, action: PayloadAction<number>) => {
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = action.payload
    },
    updateOverviewOrderBy: (
      state,
      action: PayloadAction<CommissionBenchmarkOverviewColumnsEnum>,
    ) => {
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = 1
      state.overviewPagination.order = SortDirectionCapital.desc
      state.overviewPagination.orderBy = action.payload
    },
    updateOverviewSortOrder: (state, action: PayloadAction<SortDirectionCapital>) => {
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = 1
      state.overviewPagination.order = action.payload
    },
    updateOverviewSearch: (state, action: PayloadAction<string>) => {
      state.overviewLoading = LoadingStatusEnum.Pending
      state.overviewPagination.page = 1
      state.overviewPagination.search = action.payload
    },
    resetAccountDetails: (state) => {
      state.expandedAccounts = []
      state.details = {}
    },
  },
  extraReducers: (builder) => {
    addExtraReducer(
      builder,
      GetCommissionBenchmarkReference,
      GetCommissionBenchmarkReferenceReducer,
    )
    addExtraReducer(builder, GetCommissionBenchmarkOverview, GetCommissionBenchmarkOverviewReducer)
    addExtraReducer(
      builder,
      GetCommissionBenchmarkAccountBreakdown,
      GetCommissionBenchmarkAccountBreakdownReducer,
    )
  },
})

export const {
  toggleAccountExpanded,
  toggleAccountDetailOnlyWorse,
  setAccountDetailSortBy,
  toggleOverviewFilter,
  updateOverviewPageSize,
  updateOverviewPage,
  updateOverviewOrderBy,
  updateOverviewSortOrder,
  updateOverviewSearch,
  resetAccountDetails,
} = CommissionBenchmarkSlice.actions

export default CommissionBenchmarkSlice.reducer
